import { useState, useEffect } from "react"

// In-memory cache for images as base64
const imageCache = new Map<string, string>()
// Track ongoing requests to prevent duplicates
const ongoingRequests = new Map<string, Promise<string>>()

export default function useImageCache(src?: string) {
  const [cachedSrc, setCachedSrc] = useState<string | undefined>(
    src ? imageCache.get(src) : undefined,
  )
  const [isLoading, setIsLoading] = useState<boolean>(
    !!src && !imageCache.has(src),
  )
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!src) {
      setCachedSrc(undefined)
      setIsLoading(false)
      return
    }

    // Check if image is already in cache
    if (imageCache.has(src)) {
      setCachedSrc(imageCache.get(src))
      setIsLoading(false)
      return
    }

    // Set loading state
    setIsLoading(true)

    // Check if there's already an ongoing request for this image
    if (!ongoingRequests.has(src)) {
      // Create a new request and store it
      const request = fetch(src)
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Failed to fetch image: ${response.status} ${response.statusText}`,
            )
          }
          return response.blob()
        })
        .then((blob) => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(blob)
          })
        })
        .then((base64String) => {
          // Store base64 in cache
          imageCache.set(src, base64String)
          // Remove from ongoing requests
          ongoingRequests.delete(src)
          return base64String
        })
        .catch((err) => {
          // Remove from ongoing requests on error too
          ongoingRequests.delete(src)
          throw err
        })

      // Store the promise
      ongoingRequests.set(src, request)
    }

    // Use the existing or new request
    ongoingRequests
      .get(src)!
      .then((base64String) => {
        setCachedSrc(base64String)
        setIsLoading(false)
      })
      .catch((err) => {
        setError(err)
        setIsLoading(false)
      })

    return () => {
      // Cleanup if component unmounts during fetch
      // Note: We don't cancel the fetch as other components might need it
    }
  }, [src])

  return { cachedSrc, isLoading, error }
}
